import { InvoicePaymentFilter, InvoicePaymentStatus } from '../models/invoice-payment-status';
import {Option, TableComponentProps, TurnoverFilter} from '../models/table-models';
import { CustomStepWizardNav } from '../components/CustomStepWizardNav';
import React, {Suspense, useContext, useEffect, useMemo, useState} from 'react';
import { useSearchParams } from 'react-router-dom';
import OpeningBalances from './OpeningBalances';
import { TableSuspenseContainer } from './passports/Passports';
import { Invoices } from './Invoices';
import { Turnovers } from './Turnovers';
import Select, {GroupBase, OnChangeValue} from 'react-select';
import {resourceCache, useAsyncResource} from 'use-async-resource';
import { useGetAllOilPassports, useGetAllShortDevicePassports } from '../utils/warehouses-hooks';
import { OilPassport, ShortDevicePassport } from '../models/warehouse-models';
import { useTranslation } from 'react-i18next';
import {AuthContext} from "../context/AuthContext";
import { NewRequestProvider } from '../context/NewRequestContext';
import {useGetAllTurnovers, useGetUsers} from "../utils/users-hooks";
import {useGetClientBranches, useGetInvoices} from "../utils/clients-hooks";
import {fetchMapBranches} from "../utils/dashboard-hooks";
import {AllDocuments} from "./AllDocuments";
import {Sales} from "./Sales";
import {Spinner} from "react-bootstrap";
import {stringsToOptions, stringToOption} from "../components/FormMultiSelect";
import {invoicePayment, invoiceStatuses} from "../utils/Enums/InvoiceFields";
import {DepositTurnoverReport} from './turnover/DepositTurnoverReport';

export const InvoicesPage = ({ filter, pagination, setFilter, setGeneralFiltersVisible }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const {t: invoiceT} = useTranslation('translation', {keyPrefix: 'invoice'});
    const {t: tableT} = useTranslation('translation', {keyPrefix: 'table'});

    const [allDevices] = useAsyncResource(useGetAllShortDevicePassports, false);
    const devicesPassports = allDevices() as ShortDevicePassport[];
    const [allOils] = useAsyncResource(useGetAllOilPassports, false);
    const oilPassports = allOils();
    const [passportIdsFilter, setPassportIdsFilter] = useState([] as string[]);
    const [oilIdsFilter, setOilIdsFilter] = useState([] as string[]);
    const {hasPermission} = useContext(AuthContext);
    const [params, setParams] = useSearchParams();
    const tabs = hasPermission(['ADMIN', 'FINANCIAL'])
        ? ['duties', 'sales', 'turnover', 'allDocuments', 'depositTurnover', 'opening-balances']
        : ['duties', 'sales', 'opening-balances'];
    const activeTab = tabs.includes(params.get('tab') || '') ? params.get('tab')! : 'duties';
    const currentStep = ['duties', 'sales', 'turnover', 'allDocuments', 'depositTurnover', 'opening-balances'].indexOf(activeTab) + 1;
    useEffect(() => {
        setGeneralFiltersVisible?.(currentStep < 5);
        return () => setGeneralFiltersVisible?.(true);
    }, [currentStep, setGeneralFiltersVisible]);
    // Табовете: 1 задължения, 2 продажби, 3 оборот, 4 всички документи. Част от филтрите нямат смисъл на
    // всеки от тях и дотук просто се подминаваха мълчаливо - сега се изключват, за да си личи.
    const isSalesTab = currentStep === 2;
    const isDocumentTab = currentStep >= 3;

    const [getAllUsers] = useAsyncResource(useGetUsers, {}, 'ADMIN,DISTRIBUTOR,INSTALLER,COORDINATOR,FINANCIAL');
    const { items: users } = getAllUsers();
    const [filterPaidType, setFilterPaidType] = useState('all');
    const [filterPaid, setFilterPaid] = useState<InvoicePaymentFilter | undefined>(InvoicePaymentStatus.Unpaid);
    const [turnoverPaid, setTurnoverPaid] = useState<InvoicePaymentFilter | undefined>(InvoicePaymentFilter.All);
    const [branchId, setBranchId] = useState<string>();
    const [userId, setUserId] = useState<string>();
    const [documentType, setDocumentType] = useState<string>('');
    const [saleType, setSaleType] = useState<string>('');

    const onPassportFilterChange = (values: OnChangeValue<ShortDevicePassport, true>) => {
        const passportIds = values.map((value) => String(value.id)) ?? [];
        setPassportIdsFilter(passportIds);
    };
    const onOilFilterChange = (values: OnChangeValue<OilPassport, true>) => {
        const oilIds = values.map((value) => String(value.id)) ?? [];
        setOilIdsFilter(oilIds);
    };

    const invoiceFilter: TurnoverFilter = {
        ...filter,
        oilIds: oilIdsFilter,
        passportIds: passportIdsFilter,
        // Обектът е чужд на продажбата - тя е към клиент и няма обект - затова не пътува на този таб.
        // Дотук се пращаше и се подминаваше мълчаливо: избираш обект и списъкът не се променяше.
        id: isSalesTab ? undefined : branchId,
        paid: activeTab === 'turnover' ? turnoverPaid : filterPaid,
        paidType: filterPaidType,
        userId: userId,
        branchIds: !isSalesTab && branchId ? [branchId] : undefined,
        userIds: userId ? [userId] : undefined,
        paymentMethod: filterPaidType,
        type: documentType,
        // Наем/Продажба разделя оборота и документите. Задълженията са само наеми, продажбите - само
        // продажби, така че там изборът няма какво да отсее.
        saleType: isDocumentTab ? saleType : '',
    };

    const refetchEntitiesHook = async () => {
        resourceCache(useGetAllTurnovers).clear();
        resourceCache(useGetInvoices).clear();
        // REMOVED: resourceCache(fetchMapBranches).clear();
        // Invoice refetch doesn't modify branch data, no need to refetch branches
    };


    const handleStepChange = ({ previousStep, activeStep }:{previousStep:number, activeStep:number}) => {
        const next = new URLSearchParams(params);
        next.set('tab', tabs[activeStep - 1]);
        setParams(next);
        // Продажбите нямат частични плащания - опцията изчезва от списъка, така че оставена избрана би
        // показала празен избор и пълен списък. Връща се на „неплатени", както се отваря екранът.
        if (activeStep === 2 && filterPaid === InvoicePaymentFilter.Partial) {
            resourceCache(useGetInvoices).clear();
            setFilterPaid(InvoicePaymentStatus.Unpaid);
        }
    };

    return (
        <>
            {currentStep < 5 && <>
            <div className='d-flex w-100 gap-1 flex-row justify-content-between mb-3'>
                <div className='d-flex w-100 gap-1 justify-content-between mb-3'>
                    <div className='w-25'>
                        <h5 className='mb-2'>{t('distributors')}</h5>
                        <Select
                            className='w-100'
                            isClearable
                            options={users}
                            onChange={(user) => setUserId(user ? String(user?.id) : undefined)}
                            getOptionLabel={(user) => user.name}
                            getOptionValue={(user) => String(user.id)}
                            placeholder={t('distributors')}
                            backspaceRemovesValue
                            menuPortalTarget={document.body}
                        />
                    </div>
                    <div className='w-25'>
                        <h5 className='mb-2'>{t('branch')}</h5>
                        <Suspense fallback={<Spinner animation='border'/>}>
                            <div title={isSalesTab ? t('filterNotForSales') : undefined}>
                                <BranchSelect setBranch={setBranchId} disabled={isSalesTab}/>
                            </div>
                        </Suspense>
                    </div>
                    <div className='w-25'>
                        <h5 className='mb-2'>{t('paymentMethod')}</h5>
                        <Select
                            options={stringsToOptions(invoicePayment)}
                            defaultValue={stringToOption(invoicePayment[0])}
                            getOptionLabel={({ name }) => t(name)}
                            getOptionValue={({ value }) => value}
                            onChange={(option: Option | null) => {
                                resourceCache(useGetInvoices).clear();
                                setFilterPaidType(option ? option.value : '');
                            }}
                        />
                    </div>
                    <div className='w-25'>
                        <h5 className='mb-2'>{t('state')}</h5>
                        <Select
                            options={stringsToOptions(
                                isSalesTab ? invoiceStatuses.filter((status) => status !== InvoicePaymentFilter.Partial) : invoiceStatuses
                            )}
                            value={activeTab === 'turnover'
                                ? turnoverPaid ? stringToOption(turnoverPaid) : null
                                : filterPaid ? stringToOption(filterPaid) : null}
                            getOptionLabel={({ name }) => t(name)}
                            getOptionValue={({ value }) => value}
                            onChange={(option: Option | null) => {
                                resourceCache(useGetInvoices).clear();
                                (activeTab === 'turnover' ? setTurnoverPaid : setFilterPaid)(
                                    invoiceStatuses.find(status => status === option?.value));
                            }}
                        />
                    </div>
                </div>
                <div className='w-25'>
                    <h5 className='mb-2'>{t('typeDocument')}</h5>
                    <Select
                        className='w-100 me-3'
                        isClearable
                        options={[
                            {name: 'INVOICE', value: 'INVOICE'},
                            {name: 'RECEIPT', value: 'RECEIPT'},
                            {name: 'STORNO', value: 'STORNO'},
                            {name: 'CREDIT_NOTE', value: 'CREDIT_NOTE'},
                            {name: 'DUPLICATED', value: 'DUPLICATED'},
                            {name: 'CANCELLED', value: 'CANCELLED'},
                        ]}
                        onChange={(value: Option | null) => setDocumentType(value?.value ?? '')}
                        getOptionLabel={({value}) => invoiceT(value.toLowerCase())}
                        getOptionValue={({value}) => value}
                        placeholder={tableT('documentType')}
                        backspaceRemovesValue
                        menuPortalTarget={document.body}
                    />
                </div>
                <div className='w-25' title={!isDocumentTab ? t('filterOnlyForTurnoverAndDocuments') : undefined}>
                    <h5 className='mb-2'>{t('saleType')}</h5>
                    <Select
                        className='w-100 me-3'
                        isClearable
                        isDisabled={!isDocumentTab}
                        options={[
                            { name: 'sale', value: 'SALE' },
                            { name: 'rental', value: 'RENTAL' },
                        ]}
                        onChange={(value: Option | null) => setSaleType(value?.value ?? '')}
                        getOptionLabel={({ name }) => t(name)}
                        getOptionValue={({ value }) => value}
                        placeholder={t('saleType')}
                        backspaceRemovesValue
                        menuPortalTarget={document.body}
                    />
                </div>
            </div>

            <div className='d-flex justify-content-between w-100'>
                <Select
                    className='w-30'
                    isMulti
                    isClearable
                    options={devicesPassports}
                    onChange={onPassportFilterChange}
                    getOptionLabel={(device) => device.name}
                    getOptionValue={(device) => String(device.id)}
                    placeholder={t('devices')}
                    backspaceRemovesValue
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <Select
                    className='w-30'
                    isMulti
                    isClearable
                    options={oilPassports}
                    onChange={onOilFilterChange}
                    getOptionLabel={(oil) => oil.name}
                    getOptionValue={(oil) => String(oil.id)}
                    placeholder={t('oils')}
                    backspaceRemovesValue
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
            </div>

            </>}
            <div className='w-100 full-width-table'>
                <CustomStepWizardNav steps={tabs.map(tab => tab === 'opening-balances' ? 'openingBalances' : tab)}
                    currentStep={tabs.indexOf(activeTab) + 1}
                    goToStep={step => handleStepChange({ previousStep: currentStep, activeStep: Number(step) })} />
                <TableSuspenseContainer>
                    {/* Провайдърът е заради бутона за отваряне на обекта в колоната с действията -
                        диалогът на обекта се кара от NewRequestContext. Стои само около този таб, за да
                        не товари останалите. */}
                    {currentStep === 1 && (
                        <NewRequestProvider>
                            <Invoices refetchTurnovers={refetchEntitiesHook} filter={invoiceFilter} {...{ pagination, setFilter }} />
                        </NewRequestProvider>
                    )}
                </TableSuspenseContainer>
                <TableSuspenseContainer>
                    {currentStep === 2 && <Sales refetchTurnovers={refetchEntitiesHook} filter={invoiceFilter} {...{ pagination, setFilter }} />}
                </TableSuspenseContainer>
                {/*<TableSuspenseContainer>*/}
                {/*    {currentStep==2 && <BranchReports filter={invoiceFilter} {...{ pagination, setFilter }}></BranchReports>}*/}
                {/*</TableSuspenseContainer>*/}
                <TableSuspenseContainer>
                    {currentStep === 3 && hasPermission(["ADMIN","FINANCIAL"]) && <Turnovers refetchEntitiesHook={refetchEntitiesHook} filter={invoiceFilter} {...{ pagination, setFilter }}></Turnovers>}
                </TableSuspenseContainer>
                {/*<TableSuspenseContainer>*/}
                {/*    {currentStep==3 && hasPermission(["ADMIN","FINANCIAL"]) && <InvoicelessDocuments refetchEntitiesHook={refetchEntitiesHook} filter={invoiceFilter} {...{ pagination, setFilter }}></InvoicelessDocuments>}*/}
                {/*</TableSuspenseContainer>*/}
                <TableSuspenseContainer>
                    {currentStep === 4 && hasPermission(["ADMIN","FINANCIAL"]) && <AllDocuments refetchEntitiesHook={refetchEntitiesHook} filter={invoiceFilter} {...{ pagination, setFilter }}></AllDocuments>}
                </TableSuspenseContainer>
                <TableSuspenseContainer>
                    {currentStep === 5 && hasPermission(['ADMIN', 'FINANCIAL']) && <DepositTurnoverReport users={users.flatMap(user => user.id == null ? [] : [{id: user.id, name: user.name}])}/>}
                </TableSuspenseContainer>
                {currentStep === 6 && <OpeningBalances />}
            </div>
        </>
    );
};


/** Под две букви не се търси, а над петдесет съвпадения не се рисуват. Обектите на прода са 13 451. */
const MIN_BRANCH_SEARCH_LENGTH = 2;
const MAX_BRANCH_SEARCH_RESULTS = 50;

/**
 * Филтърът по обект.
 *
 * Дотук цялата структура се строеше наново на всеки render - единайсет хиляди групи с тринайсет хиляди
 * вложени опции - а отсяването смъкваше малки букви за всяка опция на всеки натиснат клавиш и рисуваше
 * всяко съвпадение в страницата. Сега плоският списък се строи веднъж, обхождането спира на петдесетото
 * съвпадение, а групирането по клиент се прави само върху тези петдесет, за да остане както изглеждаше.
 */
const BranchSelect: React.FC<{ setBranch: (branch: string) => void; disabled?: boolean }> = ({ setBranch, disabled }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [getAllMapBranches] = useAsyncResource(useGetClientBranches, []);
    const clientBranches = getAllMapBranches();
    const [search, setSearch] = useState('');

    const allBranches = useMemo(
        () =>
            clientBranches.flatMap(({ companyName, eik, branchList }) =>
                branchList.map(({ id, branchName }) => ({
                    companyName,
                    option: {
                        name: branchName,
                        value: String(id),
                        customSearch: `${companyName} ${branchName} ${eik ?? ''}`.toLowerCase(),
                    } as Option,
                }))
            ),
        [clientBranches]
    );

    const groupedMatches = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (query.length < MIN_BRANCH_SEARCH_LENGTH) return [];

        const groups = new Map<string, Option[]>();
        let found = 0;
        for (const { companyName, option } of allBranches) {
            if (!option.customSearch?.includes(query)) continue;

            const group = groups.get(companyName);
            if (group) group.push(option);
            else groups.set(companyName, [option]);

            if (++found === MAX_BRANCH_SEARCH_RESULTS) break;
        }

        return Array.from(groups, ([companyName, options]) => ({
            label: `${t('client')}: ${companyName}`,
            options,
        })) as GroupBase<Option>[];
    }, [allBranches, search, t]);

    return (
        <Select<Option, false, GroupBase<Option>>
            className='w-100 me-3'
            isDisabled={disabled}
            isClearable
            options={groupedMatches}
            inputValue={search}
            onInputChange={setSearch}
            filterOption={null}
            noOptionsMessage={() => (search.trim().length < MIN_BRANCH_SEARCH_LENGTH ? null : t('noData'))}
            onChange={(branch) => {
                setBranch(branch ? branch.value : ''); // Handle null when cleared
            }}
            getOptionLabel={(branch) => branch.name}
            getOptionValue={(branch) => String(branch.value)}
            placeholder={t('enterNameOrEik')}
            backspaceRemovesValue
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
    );
};
